import React, { useRef, useEffect, useState } from "react";
import { PopoverTrigger, Popover, PopoverBody, PopoverContent, Portal, PopoverHeader, useColorModeValue } from "@chakra-ui/react";
import { ProjectAnalysisPopoverContent } from "./ProjectAnalysisPopoverContent";
import { ProjectAnalysisPopoverHeader } from "./ProjectAnalysisPopoverHeader";

interface ProjectAnalysisPopoverProps {
  children: React.ReactNode;
  projectId?: string;
  openProjectButton?: boolean;
  disablePopover?: boolean;
  useReportPlaceholder?: boolean;
  companyLogoUrl?: string;
  companyDescription?: string;
}

export const ProjectAnalysisPopover: React.FC<ProjectAnalysisPopoverProps> = ({
  children,
  projectId,
  openProjectButton,
  disablePopover,
  useReportPlaceholder = false,
  companyLogoUrl,
  companyDescription = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const popoverBgColor = useColorModeValue("white", "gray.700");
  const tabPanelBgColor = useColorModeValue("gray.700", "gray.900");

  // Create a high z-index container for the portal
  useEffect(() => {
    if (!portalContainer) {
      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.top = "0";
      container.style.left = "0";
      container.style.width = "100%";
      container.style.height = "100%";
      container.style.pointerEvents = "none";
      container.style.zIndex = "9999";
      container.id = "project-analysis-popover-portal";
      document.body.appendChild(container);
      setPortalContainer(container);
    }

    return () => {
      if (portalContainer && document.body.contains(portalContainer)) {
        document.body.removeChild(portalContainer);
        setPortalContainer(null);
      }
    };
  }, [portalContainer]);

  if (disablePopover) {
    return <>{children}</>;
  }

  return (
    <Popover trigger="hover" isLazy>
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal containerRef={{ current: portalContainer }}>
        <PopoverContent
          width="100%"
          boxShadow={"lg"}
          maxWidth={"50rem"}
          className="ch-project-analysis-popover"
          style={{ pointerEvents: "auto" }}>
          <PopoverHeader bgColor={popoverBgColor} fontSize="md" borderTopRadius={"md"}>
            <ProjectAnalysisPopoverHeader projectId={projectId} openProjectButton={openProjectButton} companyLogoUrl={companyLogoUrl} />
          </PopoverHeader>
          <PopoverBody
            p="0"
            width="100%"
            ref={containerRef}
            backgroundColor={tabPanelBgColor}
            borderBottomRadius={"md"}
            borderTopRadius={useReportPlaceholder ? "md" : "none"}>
            <ProjectAnalysisPopoverContent
              projectId={projectId}
              useReportPlaceholder={useReportPlaceholder}
              companyDescription={companyDescription}
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
